import React from "react";
import {
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  Box,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  StyledHelperText,
  StyledInputLabel,
  StyledSelect,
} from "./StyledSelect";

const MUIMultipleSelect = React.forwardRef(
  (
    {
      label,
      options,
      value = [],
      onChange,
      placeholder,
      error,
      helperText,
      onBlur,
      disabled,
      required,
      id,
      objectOptions = false,
      name,
      ...otherProps
    },
    ref
  ) => {
    const handleChange = (event) => {
      if (!objectOptions) {
        const selectedValues = event.target.value;
        onChange?.({ target: { value: selectedValues } });
      }
    };

    const handleObjectOptionChange = (event, val) => {
      event.stopPropagation();
      const isChecked = event.target.checked;
      let newValue;      
      if (isChecked) {
        const optionToAdd = options?.find((opt) => opt.id === val.id) || val;
        newValue = [...(value || []), optionToAdd];
      } else {
        newValue = (value || []).filter((item) => item.id !== val.id);
      }
      
      onChange?.({ target: { value: newValue } });
    };

    const normalizedValue = React.useMemo(() => {
      if (objectOptions && options) {
        const valueArray = Array.isArray(value) ? value : [];
        if (valueArray?.length > 0) {
          return valueArray?.map((valItem) => {
            const matchingOption = options?.find((opt) => opt.id === valItem?.id);
            return matchingOption || valItem;
          });
        }
        return [];
      }
      return Array.isArray(value) ? value : [];
    }, [objectOptions, options, value]);

    const renderOptions = () => {
      if (objectOptions) {
        const currentValue = objectOptions ? normalizedValue : value;
        return options?.map((val, idx) => {
          const isChecked = currentValue?.some((item) => item?.id === val?.id) || false;
          return (
            <MenuItem 
              key={val.id || idx} 
              value={val} 
              onClick={(e) => {
                const isCheckboxClick = 
                  e.target.type === 'checkbox' || 
                  e.target.tagName === 'INPUT' ||
                  e.target.closest('input[type="checkbox"]') ||
                  e.target.closest('span[class*="MuiCheckbox"]');
                
                if (!isCheckboxClick) {
                  e.preventDefault();
                  e.stopPropagation();
                  const syntheticEvent = {
                    target: { checked: !isChecked },
                    stopPropagation: () => {},
                  };
                  handleObjectOptionChange(syntheticEvent, val);
                }
              }}
            >
              <Checkbox
                checked={isChecked}
                onChange={(event) => {
                  event.stopPropagation();
                  handleObjectOptionChange(event, val);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
              <ListItemText primary={val.name} />
            </MenuItem>
          );
        });
      } else if (options && !objectOptions) {
        return options?.map((val, idx) => (
          <MenuItem key={idx} value={val}>
            <Checkbox checked={value?.indexOf(val) > -1} />
            <ListItemText primary={val} />
          </MenuItem>
        ));
      }
      return null;
    };

    const renderValue = (selected) => {
      if (!selected || selected?.length === 0) {
        return <span className="placeholder">{placeholder}</span>;
      }

      const numTags = selected?.length;
      const limitTags = 1;

      return (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {selected?.slice(0, limitTags)?.map((option, index) => (
            <Chip
              key={objectOptions ? option.id : index}
              label={objectOptions ? option.name : option}
              sx={{
                maxWidth: "100px",
                borderRadius: "4px",
                height: "26px",
                overflow: "hidden",
              }}
            />
          ))}
          {numTags > limitTags && ` +${numTags - limitTags}`}
        </Box>
      );
    };

    return (
      <StyledSelect>
        <StyledInputLabel>
          {label}
          {required && <span className="required"> * </span>}
        </StyledInputLabel>
        <Select
          ref={ref}
          id={id}
          name={name}
          disabled={disabled}
          displayEmpty
          fullWidth
          multiple
          labelId="mui-multi-select-label"
          value={objectOptions ? normalizedValue : value}
          onChange={handleChange}
          onBlur={onBlur}
          IconComponent={KeyboardArrowDownIcon}
          error={error}
          renderValue={renderValue}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 200,
                overflowY: "auto",
              },
            },
          }}
          {...otherProps}
        >
          {renderOptions()}
        </Select>
        {helperText && <StyledHelperText>{helperText}</StyledHelperText>}
      </StyledSelect>
    );
  }
);

MUIMultipleSelect.displayName = "MUIMultipleSelect";

export default MUIMultipleSelect;

